/* eslint-disable no-console */

import { register } from 'register-service-worker';
import { getPendingTokenRefresh } from '@/apiClient';
import { hardNavigate } from '@/utils';
import { markUpdateReady } from '@/utils/appUpdate';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

// If a token refresh is in flight, leaving the page right now would tear it down before
// the rotated refresh_token gets persisted to localStorage, even though the backend
// already consumed the old one — the next load would then retry with a stale token and
// get rejected as reused/invalid. Wait for it (bounded, in case it never settles).
const navigateWhenSafe = (navigate: () => void): void => {
  const pendingRefresh = getPendingTokenRefresh();
  if (pendingRefresh) {
    void Promise.race([
      pendingRefresh.catch(() => undefined),
      new Promise((resolve) => setTimeout(resolve, 10000)),
    ]).then(navigate);
  } else {
    navigate();
  }
};

if (process.env.NODE_ENV === 'production') {
  register(`${process.env.BASE_URL}service-worker.js`, {
    ready() {
      console.log(
        'App is being served from cache by a service worker.\n' +
        'For more details, visit https://goo.gl/AFskqB',
      );
    },
    registered(registration) {
      // Browsers only check for a new service worker on a full page load, and Sophia
      // stays open for days without one — so users kept running old builds. Check
      // periodically and whenever the tab becomes visible again.
      const checkForUpdate = () => {
        registration.update().catch((error) => console.error('Service worker update check failed:', error));
      };
      setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
    },
    cached() {
      console.log('Content has been cached for offline use.');
    },
    updated() {
      console.log('New content is available.');
      // skipWaiting is set in workbox config, so the new SW is already active. A hidden
      // tab can reload right away; a visible one switches on the next navigation so an
      // unsaved form is never thrown away.
      if (document.visibilityState === 'hidden') {
        navigateWhenSafe(() => window.location.reload());
      } else {
        markUpdateReady((href) => navigateWhenSafe(() => hardNavigate(href)));
      }
    },
    offline() {
      console.log('No internet connection found. App is running in offline mode.');
    },
    error(error) {
      console.error('Error during service worker registration:', error);
    },
  });
}
